<template>
  <div class="w-full">
    <div v-if="loading" class="flex h-96 w-full items-center justify-center">
      <TheLoader />
    </div>

    <div v-else class="mb-10 h-full w-full">
      <div
        class="mx-auto w-full max-w-content-container px-4 lg:px-14 2xl:px-0"
      >
        <BackButton
          show-icon-back
          title="Back to Data & Reports"
          class="my-5"
          :route="route"
        />
      </div>
      <div
        class="relative flex h-full w-full flex-col border-t-4 border-p-500 bg-s-900"
      >
        <VimeoPlayer
          v-if="vimeoVideo"
          :source="vimeoSource"
          :autoplay="startPlay"
          video-ratio="4:2"
          class="h-full w-full"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TheLoader from '~/components/TheLoader.vue'
import BackButton from '~/components/shared/common/BackButton.vue'
import VimeoPlayer from '~/components/video/VimeoPlayer.vue'

import { useGetVimeoData } from '~/composables/useGetVimeoData'

import { reformat } from '~/utils/date'
import { logError } from '~/utils/log-error'

const props = defineProps<{
  reportId: string
  reportCategory: string
}>()

const { $downloadManager } = useNuxtApp()
const { vimeoSource, vimeoVideo, getVimeoData } = useGetVimeoData()

const loading = ref(true)
const startPlay = ref(false)
const videoTitle = ref('')
const videoDate = ref(null)

const route = computed(() => {
  const { path, query } = useRoute()
  const queryParams = new URLSearchParams(
    query as Record<string, string>,
  ).toString()
  const routePath = path.replace('/forecast/', '/forecasts/')
  const basePath = `/${routePath.split('/').slice(1, 4).join('/')}`
  return queryParams ? `${basePath}?${queryParams}` : basePath
})

async function onInit() {
  try {
    if (props.reportCategory && props.reportId) {
      const report = await $downloadManager.getDataFileByIdAndCategory(
        props.reportCategory,
        props.reportId,
      )
      videoTitle.value = report.post_title
      videoDate.value = reformat(report.post_date, 'MMM dd, yyyy')
      const vimeoId = report.file_name
      await getVimeoData(vimeoId)
    }
  } catch (error) {
    logError(error)
    if (error?.data?.status === 404 || error?.message?.includes('not found')) {
      throw showError({
        statusCode: 404,
        message: 'Video not found',
      })
    }
    if (error?.data?.status === 403 || error?.code === 'rest_forbidden') {
      await navigateTo(route.value)
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  onInit()
})
</script>
